// src/host/HostLayout.js
import React, { useMemo, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";

export default function HostLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Mark "More" active for routes that live in the drawer (or anything beyond the 4 main tabs)
  const moreActive = useMemo(() => {
    const p = location.pathname || "";
    return (
      p.startsWith("/host/access") ||
      p.startsWith("/host/payouts") ||
      p.startsWith("/host/settings")
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden border-r border-neutral-200 bg-white lg:block">
        <div className="p-5">
          <Brand imageClassName="h-12 w-auto" className="mb-6" />
          <DesktopNav />
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm active:scale-[0.99]"
        >
          Menu
        </button>

        <Brand imageClassName="h-10 w-auto" className="mb-0" />

        <div className="w-[64px]" aria-hidden />
      </header>

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="p-5">
          <Brand imageClassName="h-12 w-auto" className="mb-5" />
          <MobileNav onNavigate={() => setDrawerOpen(false)} />
        </div>
      </MobileDrawer>

      {/* Main content */}
      <main
        className="px-4 py-4 pb-28 sm:px-6 sm:py-6 lg:pb-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 96px)" }}
      >
        <Outlet />
      </main>

      {/* Mobile bottom tabs */}
      <MobileTabBar
        onMore={() => setDrawerOpen(true)}
        moreActive={moreActive}
      />
    </div>
  );
}

/* ---------- Brand ---------- */
function Brand({ className = "", imageClassName = "h-10 w-auto" }) {
  return (
    <Link
      to="/"
      className={`flex items-center justify-center lg:justify-start ${className}`}
      aria-label="Go home"
    >
      <picture>
        <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
        <img
          src="/YoureWelcome Logo.png"
          alt="You’re Welcome"
          className={imageClassName}
          draggable={false}
        />
      </picture>
    </Link>
  );
}

/* ---------- Desktop nav ---------- */
function DesktopNav() {
  return (
    <nav className="mt-2 grid gap-1 text-sm">
      <NavItem to="/host" end>
        Dashboard
      </NavItem>
      <NavItem to="/host/listings">Listings</NavItem>
      <NavItem to="/host/calendar">Calendar</NavItem>
      <NavItem to="/host/bookings">Bookings</NavItem>
      <NavItem to="/host/access">Check-in &amp; access</NavItem>
      <NavItem to="/host/payouts">Payouts</NavItem>
      <NavItem to="/host/settings">Settings</NavItem>
    </nav>
  );
}

function NavItem({ to, end, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "rounded-xl px-3 py-2 transition",
          isActive
            ? "bg-neutral-100 font-semibold text-neutral-900"
            : "text-neutral-700 hover:bg-neutral-50",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

/* ---------- Mobile nav (drawer) ---------- */
function MobileNav({ onNavigate }) {
  const linkCls =
    "rounded-xl px-3 py-2.5 text-sm hover:bg-neutral-50 active:bg-neutral-100 block";

  return (
    <nav className="grid gap-1">
      <NavLink to="/host" end className={linkCls} onClick={onNavigate}>
        Dashboard
      </NavLink>
      <NavLink to="/host/listings" className={linkCls} onClick={onNavigate}>
        Listings
      </NavLink>
      <NavLink to="/host/calendar" className={linkCls} onClick={onNavigate}>
        Calendar
      </NavLink>
      <NavLink to="/host/bookings" className={linkCls} onClick={onNavigate}>
        Bookings
      </NavLink>

      <div className="my-2 h-px bg-neutral-200" />

      <NavLink to="/host/access" className={linkCls} onClick={onNavigate}>
        Check-in &amp; access
      </NavLink>
      <NavLink to="/host/payouts" className={linkCls} onClick={onNavigate}>
        Payouts
      </NavLink>
      <NavLink to="/host/settings" className={linkCls} onClick={onNavigate}>
        Settings
      </NavLink>
    </nav>
  );
}

/* ---------- Mobile drawer ---------- */
function MobileDrawer({ open, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 left-0 w-[82vw] max-w-[340px] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <span className="text-sm font-semibold">Menu</span>
          <button
            onClick={onClose}
            className="rounded-xl border border-neutral-200 bg-white px-3 py-1.5 text-xs active:scale-[0.99]"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Mobile bottom tab bar ---------- */
function MobileTabBar({ onMore, moreActive }) {
  const Item = ({ to, label, icon: Icon, end }) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex flex-1 flex-col items-center justify-center gap-1 py-2",
          "text-[11px] leading-none",
          isActive ? "text-neutral-900" : "text-neutral-500",
        ].join(" ")
      }
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-neutral-200 bg-white/95 backdrop-blur lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <Item to="/host" end label="Home" icon={HomeIcon} />
      <Item to="/host/listings" label="Listings" icon={LayersIcon} />
      <Item to="/host/calendar" label="Calendar" icon={CalendarIcon} />
      <Item to="/host/bookings" label="Bookings" icon={ClipboardIcon} />
      <button
        type="button"
        onClick={onMore}
        className={[
          "flex flex-1 flex-col items-center justify-center gap-1 py-2",
          "text-[11px] leading-none",
          moreActive ? "text-neutral-900" : "text-neutral-500",
        ].join(" ")}
        aria-label="More"
      >
        <MoreIcon className="h-5 w-5" />
        <span>More</span>
      </button>
    </nav>
  );
}

/* ---------- tiny inline icons (no deps) ---------- */
function iconBase(props) {
  return {
    ...props,
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    strokeWidth: 2,
  };
}
function HomeIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M3 11l9-7 9 7" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}
function LayersIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 2l9 5-9 5-9-5 9-5z" />
      <path d="M3 12l9 5 9-5" />
      <path d="M3 17l9 5 9-5" />
    </svg>
  );
}
function CalendarIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function ClipboardIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <rect x="7" y="3" width="10" height="4" rx="1" />
      <rect x="5" y="7" width="14" height="14" rx="2" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  );
}
function MoreIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}
